import type {
  EngineAdapter,
  Layer,
  LayerKind,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import { MVTLayer, Tile3DLayer, TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer, GeoJsonLayer, ScatterplotLayer } from '@deck.gl/layers';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { Tiles3DLoader } from '@loaders.gl/3d-tiles';
import { declareCapabilities, type EngineCapabilities } from '@/engines/contract';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { resolveVectorFallbackColor, tokenColorToRgba } from '@/design/tokenResolver';

const DECKGL_SUPPORTED: LayerKind[] = ['raster', 'vector', '3dtiles'];

const DECKGL_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'deckgl',
  supportedLayerKinds: DECKGL_SUPPORTED,
  supportsTime: true,
  supportsTerrain: false,
  supports3DTiles: '1.1',
  supportsInterleaving: ['maplibre', 'cesium'],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'export'],
});

const VECTOR_COLOR: [number, number, number, number] = tokenColorToRgba(
  resolveVectorFallbackColor(),
  255,
);
const VECTOR_FILL_COLOR: [number, number, number, number] = [
  VECTOR_COLOR[0],
  VECTOR_COLOR[1],
  VECTOR_COLOR[2],
  80,
];
const VECTOR_POINT_COLOR: [number, number, number, number] = [
  VECTOR_COLOR[0],
  VECTOR_COLOR[1],
  VECTOR_COLOR[2],
  220,
];

export class DeckGLAdapter implements EngineAdapter {
  id = 'deckgl' as const;
  readonly capabilities: EngineCapabilities = DECKGL_CAPABILITIES;
  private mounted = false;
  private overlay: MapboxOverlay | null = null;
  private deckLayers = new Map<string, object>();
  private host: ViewerHostContext | null = null;

  supports(kind: LayerKind): boolean {
    return DECKGL_SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;
    this.mounted = true;
    const map = getMapLibreHostHandle(host.container);
    if (!map) {
      host.emit({
        type: 'error',
        message:
          'deckgl-no-primary-maplibre: no shared MapLibre handle on host container',
      });
      return;
    }

    this.overlay = new MapboxOverlay({ interleaved: true, layers: [] });
    map.addControl(this.overlay);
  }

  async unmount(): Promise<void> {
    this.deckLayers.clear();
    this.overlay?.setProps({ layers: [] });
    this.overlay = null;
    this.host = null;
    this.mounted = false;
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.mounted || !this.overlay) return;
    const deckLayer = this.createLayer(layer);
    if (!deckLayer) return;
    this.deckLayers.set(layer.id, deckLayer);
    this.syncOverlayLayers();
  }

  async removeLayer(layerId: string): Promise<void> {
    if (!this.overlay) return;
    this.deckLayers.delete(layerId);
    this.syncOverlayLayers();
  }

  _layerStats(): Record<string, number> {
    const stats: Record<string, number> = {
      raster: 0,
      vector: 0,
      '3dtiles': 0,
    };
    for (const value of this.deckLayers.values()) {
      const id = String((value as { id?: unknown }).id ?? '');
      if (id.includes('-raster-')) stats.raster += 1;
      else if (id.includes('-vector-')) stats.vector += 1;
      else if (id.includes('-3dtiles-')) stats['3dtiles'] += 1;
    }
    return stats;
  }

  private syncOverlayLayers(): void {
    if (!this.overlay) return;
    this.overlay.setProps({ layers: [...this.deckLayers.values()] } as never);
  }

  setTime(t: Date): void {
    void t;
    this.syncOverlayLayers();
  }

  private createLayer(layer: Layer): object | null {
    const minZoom = layer.visibility.minZoom ?? 0;
    const maxZoom = layer.visibility.maxZoom ?? 22;

    if (layer.kind === 'raster' && layer.endpoints.tiles) {
      return new TileLayer({
        id: `deck-raster-${layer.id}`,
        data: layer.endpoints.tiles,
        minZoom,
        maxZoom,
        tileSize: 256,
        renderSubLayers: (props: Record<string, unknown>) => {
          const tile = props.tile as {
            bbox: { west: number; south: number; east: number; north: number };
          };
          return new BitmapLayer(props, {
            id: `deck-raster-bitmap-${layer.id}`,
            image: props.data as string,
            bounds: [tile.bbox.west, tile.bbox.south, tile.bbox.east, tile.bbox.north],
          });
        },
      });
    }

    if (layer.kind === 'vector' && layer.endpoints.tiles) {
      return new MVTLayer({
        id: `deck-vector-mvt-${layer.id}`,
        data: layer.endpoints.tiles,
        minZoom,
        maxZoom,
        pickable: true,
        stroked: true,
        filled: true,
        getLineColor: VECTOR_COLOR,
        getFillColor: VECTOR_FILL_COLOR,
        lineWidthMinPixels: 1,
      });
    }

    if (layer.kind === 'vector' && layer.endpoints.features) {
      return new GeoJsonLayer({
        id: `deck-vector-geojson-${layer.id}`,
        data: layer.endpoints.features,
        minZoom,
        maxZoom,
        pickable: true,
        stroked: true,
        filled: true,
        getLineColor: VECTOR_COLOR,
        getFillColor: VECTOR_FILL_COLOR,
        lineWidthMinPixels: 1,
        pointRadiusMinPixels: 2,
      });
    }

    if (layer.kind === 'vector' && layer.endpoints.pmtiles && layer.bbox) {
      const [minX, minY, maxX, maxY] = layer.bbox;
      return new ScatterplotLayer({
        id: `deck-vector-pmtiles-anchor-${layer.id}`,
        data: [{ position: [(minX + maxX) / 2, (minY + maxY) / 2] }],
        getPosition: (d: { position: [number, number] }) => d.position,
        getRadius: 60,
        radiusUnits: 'meters',
        getFillColor: VECTOR_POINT_COLOR,
        pickable: true,
      });
    }

    if (layer.kind === '3dtiles' && layer.endpoints.tileset3d) {
      return new Tile3DLayer({
        id: `deck-3dtiles-${layer.id}`,
        data: layer.endpoints.tileset3d,
        loader: Tiles3DLoader,
      } as Record<string, unknown>);
    }

    this.host?.emit({
      type: 'error',
      message: `deckgl-unsupported-layer: ${layer.id} (${layer.kind})`,
    });
    return null;
  }
}
